import { Link } from "react-router-dom";
import { Crown, Music2, Users, Youtube } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, useContent, useSettings } from "@/lib/api";
import { DiscordIcon } from "@/components/site/UserMenu";
import { Chip, PageHero, Reveal, Skeleton, Wrap } from "@/night/ui";

export function News() {
  const { data = [], isLoading } = useContent("news");
  return (
    <>
      <PageHero en="DISPATCH" ar="أخبار المدينة" desc="الإعلانات والفعاليات ومواعيد افتتاح الأقسام، مباشرة من الإدارة." />
      {isLoading ? <Skeleton /> : !data.length ? <Wrap className="mt-16 text-center text-slate-400" data-testid="empty-state">ما فيه أخبار للحين</Wrap> : (
        <Wrap className="mt-16 max-w-5xl">
          {data.map((n, i) => (
            <Reveal key={n.id}>
              <article className="grid gap-6 border-b border-[var(--n-line)] py-12 sm:grid-cols-[160px_1fr]" data-testid={`news-${n.id}`}>
                <div><span className="f-lat text-6xl leading-none text-[var(--n-cyan)]/30">{String(i + 1).padStart(2, "0")}</span><div className="f-mono mt-3 text-xs text-slate-500">{n.date}</div></div>
                <div><Chip>{n.tag}</Chip><h2 className="f-ar mt-4 text-4xl">{n.title}</h2><p className="mt-4 whitespace-pre-line text-lg leading-9 text-slate-300">{n.body}</p></div>
              </article>
            </Reveal>
          ))}
        </Wrap>
      )}
    </>
  );
}

export function Staff() {
  const { data = [], isLoading } = useContent("staff");
  return (
    <>
      <PageHero en="COMMAND" ar="القائمون على المدينة" desc="اضغط على أي عضو عشان تروح لحسابه في الديسكورد." />
      {isLoading ? <Skeleton /> : (
        <Wrap className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.1}>
              <a href={`https://discord.com/users/${m.discord}`} target="_blank" rel="noreferrer" className="hud group relative block aspect-[3/4] overflow-hidden border border-[var(--n-line)]" data-testid={`staff-${m.id}`}>
                <img src={m.avatar} alt={m.name} className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030509] via-[#030509]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <div className="flex items-center gap-2 text-[var(--n-cyan)]">{i === 0 && <Crown size={16} />}<span className="f-lat text-sm tracking-[0.3em]">{m.role}</span></div>
                  <h2 className="f-lat mt-2 text-5xl leading-none" dir="ltr" style={{ textAlign: "right" }}>{m.name}</h2>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm text-slate-300 transition-colors group-hover:text-white"><DiscordIcon />@{m.username}</span>
                </div>
              </a>
            </Reveal>
          ))}
        </Wrap>
      )}
    </>
  );
}

export function Contact() {
  const { data: s } = useSettings();
  const Card = ({ icon, title, url, id }) => (
    <div className="border border-[var(--n-line)] bg-[var(--n-ink)] p-8" data-testid={id}>
      <div className="text-[var(--n-cyan)]">{icon}</div>
      <h3 className="f-ar mt-6 text-2xl">{title}</h3>
      {url ? <a href={url} target="_blank" rel="noreferrer" className="btn-ghost mt-6">فتح</a> : <p className="mt-3 text-sm text-slate-500">قريباً</p>}
    </div>
  );
  return (
    <>
      <PageHero en="CONTACT" ar="تواصل معنا" desc="كل الدعم والاستفسارات داخل سيرفر الديسكورد الرسمي، وفيه تكتات للشكاوى والتقديمات." />
      <Wrap className="mt-16 grid gap-6 lg:grid-cols-3">
        <Reveal className="relative overflow-hidden border border-[#5865F2]/50 bg-[#5865F2]/15 p-12 lg:col-span-3" data-testid="contact-discord">
          <div className="absolute -left-10 -top-10 text-[#5865F2]/20"><DiscordIcon size={260} /></div>
          <span className="kicker !text-[#9aa4ff]">OFFICIAL DISCORD</span>
          <h2 className="f-ar mt-4 text-5xl">سيرفر الديسكورد الرسمي</h2>
          <p className="mt-4 max-w-lg text-lg text-slate-300">الأخبار، التقديمات، الدعم الفني، وكل قنوات الأقسام.</p>
          <a href={s?.discord_invite} target="_blank" rel="noreferrer" className="btn-ice mt-8 !bg-[#5865F2] !text-white" data-testid="contact-discord-btn">انضم الآن</a>
        </Reveal>
        <Card icon={<Youtube size={34} />} title="يوتيوب" url={s?.youtube_url} id="contact-youtube" />
        <Card icon={<Music2 size={34} />} title="تيك توك" url={s?.tiktok_url} id="contact-tiktok" />
        <div className="border border-[var(--n-line)] bg-[var(--n-ink)] p-8" data-testid="contact-staff">
          <Users size={34} className="text-[var(--n-cyan)]" />
          <h3 className="f-ar mt-6 text-2xl">القائمون على المدينة</h3>
          <Link to="/staff" className="btn-ghost mt-6" data-testid="contact-staff-btn">صفحة الطاقم</Link>
        </div>
      </Wrap>
    </>
  );
}

const nameCls = (n) => (/[\u0600-\u06FF]/.test(n) ? "f-ar" : "f-lat !normal-case");

function Avatar({ p, size }) {
  return p.avatar
    ? <img src={p.avatar} alt="" className={`${size} shrink-0 rounded-full border border-[var(--n-line)] object-cover`} />
    : <div className={`${size} grid shrink-0 place-items-center rounded-full bg-[#5865F2]/30 text-[#9aa4ff]`}><DiscordIcon /></div>;
}

function Podium({ top }) {
  const order = [1, 0, 2].filter((i) => top[i]);
  return (
    <div className="grid items-end gap-4 sm:grid-cols-3" data-testid="leaderboard-podium">
      {order.map((i) => {
        const p = top[i];
        return (
          <Reveal key={p.id} delay={i * 0.12} className={`relative border bg-[var(--n-ink)] p-7 text-center ${i === 0 ? "border-amber-300/50 sm:pb-14 sm:pt-12" : "border-[var(--n-line)]"}`} data-testid={`leader-${i + 1}`}>
            {i === 0 && <Crown className="absolute right-1/2 top-4 translate-x-1/2 text-amber-300" size={22} />}
            <div className="mx-auto w-fit"><Avatar p={p} size={i === 0 ? "h-24 w-24" : "h-16 w-16"} /></div>
            <div className={`f-lat mt-4 text-5xl leading-none ${i === 0 ? "text-amber-300" : "text-[var(--n-cyan)]"}`}>{String(i + 1).padStart(2, "0")}</div>
            <div className={`mt-3 text-3xl ${nameCls(p.name)}`} dir="auto" data-testid="leader-name">{p.name}</div>
            {p.discord_name && <div className="mt-1 text-sm text-slate-500" dir="ltr">@{p.discord_name}</div>}
            <div className="f-lat mt-4 text-3xl text-white">{p.hours}<span className="ms-2 text-sm text-slate-500">H</span></div>
          </Reveal>
        );
      })}
    </div>
  );
}

export function Leaderboard() {
  const { data = [], isLoading } = useQuery({ queryKey: ["leaderboard"], queryFn: () => api.get("/leaderboard").then((r) => r.data.items), refetchInterval: 60000 });
  return (
    <>
      <PageHero en="LEGENDS" ar="الأكثر لعباً" desc="أساطير المدينة حسب ساعات اللعب الحقيقية، باسم اللاعب في السكور وحسابه في الديسكورد. تتحدث تلقائياً كل 10 دقايق." />
      {isLoading ? <Skeleton /> : !data.length ? <Wrap className="mt-16"><p className="border border-[var(--n-line)] p-12 text-center text-slate-400" data-testid="empty-state">الترتيب يبدأ أول ما اللاعبين يدخلون المدينة.</p></Wrap> : (
        <Wrap className="mt-16 max-w-5xl">
          <Podium top={data.slice(0, 3)} />
          <div className="mt-10" data-testid="leaderboard-table">
            {data.slice(3).map((p, i) => (
              <div key={p.id} className="flex items-center gap-5 border-b border-[var(--n-line)] py-4 text-slate-300" data-testid={`leader-${i + 4}`}>
                <span className="f-lat w-12 text-3xl leading-none text-slate-600">{String(i + 4).padStart(2, "0")}</span>
                <Avatar p={p} size="h-11 w-11" />
                <div className="flex-1"><div className={`text-2xl text-white ${nameCls(p.name)}`} dir="auto" style={{ textAlign: "right" }}>{p.name}</div>{p.discord_name && <div className="text-xs text-slate-500" dir="ltr">@{p.discord_name}</div>}</div>
                <span className="f-lat text-2xl text-[var(--n-cyan)]">{p.hours}<span className="ms-2 text-sm text-slate-500">H</span></span>
              </div>
            ))}
          </div>
        </Wrap>
      )}
    </>
  );
}
